import {Avatar, Button, DatePicker, Input, Modal, Radio} from 'antd'
import {getId} from 'apis/model/base'
import {group_regenerateCode_Api, organization_getGroups_Api} from 'apis'
import useAsyncAction from '../../../../modules/asyncCache/useAsyncAction'
import {RequiredSystemAdmin} from '../../../../components/RequiredAdmin'
import FieldsFactory from 'components/form/FieldsFactory'
import {ResetButton, SubmitButton} from 'components/form/FormActionBar'
import {Formik} from 'formik'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import {useOrgTypeLabel} from 'modules/local/useOrgTypeLabels'
import React, {useMemo} from 'react'
import {compose, mapProps, pure, withProps} from 'recompose'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {deepTranslate, getResponseItems, NullAsDefault, renderIf, shallowDiff} from 'views/Shared'
import {ActionModalWrapper} from "../../../../components/form/ActionModalWrapper";
import {BooleanPicker} from "../../Exam/ExamContext";
import {createSelectValue, createValue} from "../../../../components/form/utils";
import {StatusTypes} from "../../appFieldTypes";
import Translate from "../../../../modules/local/Translate";
import LookupField from "../../../../components/LookupField";
import SlateEditor from "../../../SlateEditor";
import {ModalEditorToolbar} from "../../../SlateEditor/SlateEditor";
import {convertFromString, convertToString} from "../../../SlateEditor/functions";
import {SelectWidget} from "../../custom/ReactSelectComponents";
import {CustomToggleField} from "../../../../components/form/fields/ToggleField";
import {HeaderWithIconLine} from "../../Templates/items/ListHeader";
import {BsChevronRight, BsGeoAlt, BsShieldFillExclamation} from "react-icons/bs";
import {SaintsOutlineSvg} from "../../../../components/Svgs/SaintsPlaceholder";
import dayjs from "dayjs";
import {IoCalendarOutline} from "react-icons/io5";
import {HiOutlineDocumentText} from "react-icons/hi2";
import {joinPolicyOptions, privacyOptions} from "../../../../conts/privacyOptions";
import {
    firstLetterUppercase,
    lengthBetween,
    noDoubleSpaces,
    noLeadingTrailingSpaces,
    noNumber,
    noSpecialChars,
    required,
    runRules
} from "../../../../components/form/ValidateForm";
import Pure from "../../../../components/Pure";
import {RiInformationLine} from "react-icons/ri";

const JoinCodeField = ({name, value, onChange, form}) => {
    const t = useTranslate()
    const groupId = _.get(form, 'values.id')
    const {handleAsyncAction: doRegenerate, loading} = useAsyncAction({
        apiInfo: group_regenerateCode_Api,
        onSuccess: (result, data) => {
            const newCode = _.get(data, 'response.data.join_code')
            if (newCode) onChange(createValue(name, newCode))
        },
    })
    return (
        <div className="flex items-center gap-2">
            <Input value={value} disabled className="w-full font-mono" style={{letterSpacing: '0.1em'}}/>
            <Button
                loading={loading}
                disabled={!groupId}
                onClick={() => Modal.confirm({
                    title: t('regenerate code confirm'),
                    content: t('regenerate code confirm description'),
                    okText: t('regenerate'),
                    okType: 'danger',
                    cancelText: t('cancel'),
                    onOk: () => doRegenerate({}, {id: groupId}),
                })}>
                {t('regenerate')}
            </Button>
        </div>
    )
}

const ParentDepartmentField = ({name, value, onChange, form}) => {
    const t = useTranslate()
    const organization = _.get(form, 'values.organization')
    const org_id = getId(organization) || _.get(form, 'values.organization_id')
    const deptLabel = useOrgTypeLabel(organization?.org_type, 'group', organization?.label_privacy) || t('parent group / ministry')

    const {response, isLoading} = useAsyncWithCache({
        cacheId: [organization_getGroups_Api.path, org_id].join('/'),
        apiInfo: organization_getGroups_Api,
        query: {id: org_id},
        once: true,
    })

    const options = useMemo(() => {
        const items = getResponseItems(response) || []
        return items.map(dept => ({value: dept.id, label: dept.title}))
    }, [response])

    return (
        <div className="flex flex-col gap-1">
            <span className="font-medium text-xs text-color-300 uppercase tracking-wide">
                {deptLabel}
            </span>
            <SelectWidget
                name={name}
                allowClear
                loading={isLoading}
                className="w-full"
                value={value || undefined}
                options={options}
                placeholder={t('click to select')}
                onChange={(v) => onChange(createSelectValue(name, v))}
            />
        </div>
    )
}

const FormSection = ({icon: Icon, titleKey, title, children, className}) => {
    const t = useTranslate()
    const label = title ?? t(titleKey)
    return (
        <div className={className ?? 'flex flex-col gap-2'}>
            <HeaderWithIconLine
                renderTitle={() => (
                    <div className="flex items-center gap-2">
                        <Icon size={16} className="text-color-500 shrink-0"/>
                        <span className="font-semibold text-base text-color-400 uppercase tracking-wider">
                            {label}
                        </span>
                    </div>
                )}
            />
            <div className="ml-2 flex flex-col gap-3">
                {children}
            </div>
        </div>
    )
}

const formSchema = {
    title: {
        title: 'group name',
        children: [
            {
                name: 'title',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, translate, onChange}) => ({
                        showCount: true,
                        maxLength: 120,
                        name,
                        value,
                        onChange,
                        placeholder: translate('title'),
                    })),
                    pure
                )(Input),
            },
        ],
    },
    description: {
        title: 'description',
        children: [
            {
                name: 'description',
                component: createControlledFormField({
                    showCount: true,
                    maxLength: 500,
                    rows: 6,
                    placeholder: 'description',
                    Component: Input.TextArea,
                }),
            },
        ],
    },

    active: {
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-3 gap-3',
        itemClassNames: ['col-span-1 md:col-span-2', 'col-span-1'],
        children: [
            {
                label: 'status',
                name: 'status',
                component: ({name, value, form, onChange}) => {
                    const options = Array.from(form?.values?.status_options ?? [])
                    return (
                        <Radio.Group
                            name={name}
                            value={value}
                            options={options}
                            onChange={onChange}
                            optionType="button"
                            buttonStyle="solid"
                            className="w-full custom-radio-button"
                        />
                    )
                },
            },
            {
                name: 'active',
                label: 'active',
                component: createControlledFormField({
                    Component: BooleanPicker,
                }),
            },
        ],
    },

    privacy: {
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-2 gap-3',
        children: [
            {
                label: 'privacy',
                name: 'privacy',
                component: compose(
                    withTranslate,
                    mapProps(({name, translate, onChange, value}) => ({
                        name,
                        allowClear: true,
                        className: 'w-full',
                        value: value || undefined,
                        onChange: (value) => {
                            onChange(createSelectValue('privacy', value))
                        },
                        placeholder: translate('---Choose One---'),
                        options: Object.values(privacyOptions).map(deepTranslate(translate))
                    })),
                    pure
                )(SelectWidget),
            },
            {
                label: 'join policy',
                name: 'join_policy',
                component: compose(
                    withTranslate,
                    mapProps(({name, translate, onChange, value}) => ({
                        name,
                        allowClear: true,
                        className: 'w-full',
                        value: value || undefined,
                        onChange: (value) => {
                            onChange(createSelectValue('join_policy', value))
                        },
                        placeholder: translate('---Choose One---'),
                        options: Object.values(joinPolicyOptions).map(deepTranslate(translate))
                    })),
                    pure
                )(SelectWidget),
            },
        ]
    },
    channel_post_permission: {
        title: 'posting permission',
        children: {
            name: 'channel_post_permission',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Number(value) === 3,
                    onChange: (value) => {
                        onChange(createValue(name, Boolean(value) ? 3 : 2))
                    },
                    title: translate('posting permission'),
                    description: translate("Allow changing posting permissions on the news channel; On: only administrators are allowed to post, Off: all students are allowed to post"),
                    className: "p-2 border border-dashed border-secondary rounded-lg"
                }))
            )(CustomToggleField),
        },
    },

    public: {
        children: {
            name: 'public',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Number(value) === 1,
                    onChange: (value) => {
                        onChange(createValue(name, value ? 1 : 0))
                    },
                    title: translate('show publicly'),
                    description: translate('allow others to find this group'),
                    className: "p-2 border border-dashed border-secondary rounded-lg"
                }))
            )(CustomToggleField),
        },
    },
    public_by_server: {
        children: {
            name: 'public_by_server',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Number(value) === 1,
                    onChange: (value) => {
                        onChange(createValue(name, value ? 1 : 0))
                    },
                    title: translate('show publicly (super admin)'),
                    description: translate('allow others to find this group'),
                    className: "p-2 border border-dashed border-secondary rounded-lg"
                }))
            )(CustomToggleField),
        },
    },

    group_type: {
        title: 'group type',
        children: {
            name: 'group_type',
            component: ({value}) => (
                <Translate>
                    {(t) => <Input value={t(value || 'department')} disabled className="w-full"/>}
                </Translate>
            ),
        },
    },
    idname: {
        title: 'idname',
        children: {
            name: 'idname',
            component: ({value}) => <Input value={value} disabled className="w-full font-mono"/>,
        },
    },
    channel: {
        title: 'channel',
        children: {
            name: 'channel',
            component: ({value}) => (
                <Translate>
                    {(t) => <Input value={_.get(value, 'title') || t('no data')} disabled className="w-full"/>}
                </Translate>
            ),
        },
    },
    join_code: {
        title: 'join code',
        children: {
            name: 'join_code',
            component: JoinCodeField,
        },
    },

    parent_id: {
        children: {
            name: 'parent_id',
            component: ParentDepartmentField,
        },
    },

    group_name: {
        title: 'serving in',
        children: [
            {
                name: 'group_title',
                component: ({name, value, onChange, form}) => {
                    const values = _.get(form, 'values', {})
                    return (
                        <Translate>
                            {(t) => (
                                <LookupField
                                    createIfNotExists={true}
                                    defaultValue={values?.group_title}
                                    apiPath={`/lookup/group_title/gets?group_type=group`}
                                    name={name}
                                    placeholder={t('choose or create')}
                                    handleChange={(e) => {
                                        onChange(
                                            createValue('group_title', e.target.value)
                                        )
                                    }}
                                    OptionWrapper={({children, ...props}) =>
                                        <div className="lookup-item" {...props}>
                                            {children}
                                        </div>
                                    }
                                    renderOption={(o, i) => (
                                        <div key={i} className="flex items-center flex-1 gap-2 md:gap-3">
                                            <Avatar
                                                size={40}
                                                className="flex flex-center bg-secondary-100 rounded-xl"
                                                icon={<BsGeoAlt size={20} className="text-secondary"/>}
                                            />
                                            <div className="flex flex-col gap-1 flex-1">
                                                <span className="font-bold text-sm text-color-000 leading-snug">
                                                    {o.label}
                                                </span>
                                            </div>
                                            <BsChevronRight size={16} className="text-color-500"/>
                                        </div>
                                    )}
                                />
                            )}
                        </Translate>
                    )
                },
            },
        ],
    },
    about: {
        title: 'about',
        children: [
            {
                name: 'about',
                component: createControlledFormField({
                    Component: SlateEditor,
                    ToolbarComponent: ModalEditorToolbar,
                    className: 'background border rounded-lg',
                    withProps: ({value}) => ({
                        defaultValue: value,
                    }),
                    withValues: ({resetKey}) => ({
                        resetKey,
                    }),
                }),
            },
        ],
    },
    patron_saint: {
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-2 gap-3',
        children: [
            {
                label: 'patron saint',
                name: 'patron_saint',
                component: ({name, value, onChange, form, ...restForm}) => {
                    return (
                        <Translate>
                            {(t) => (
                                <LookupField
                                    createIfNotExists={true}
                                    defaultValue={form?.values?.patron_saint_name}
                                    selectedOption={form?.values?.patron_saint}
                                    apiPath={`/lookup/saint/get-by-types`}
                                    name={name}
                                    OptionWrapper={({children, ...props}) =>
                                        <div className="lookup-item" {...props}>
                                            {children}
                                        </div>
                                    }
                                    renderOption={(o, i) => {
                                        return (
                                            <div key={i} className="flex items-start flex-1 gap-3">
                                                <Avatar
                                                    size={40}
                                                    src={o.avatar}
                                                    className="flex flex-center bg-blue-50 rounded-lg"
                                                    icon={<SaintsOutlineSvg size={20} className="text-blue-500"/>}
                                                />
                                                <div className="flex flex-col flex-1 max-lines-3">
                                                    <span
                                                        className="font-bold text-sm text-color-000 tracking-wide leading-tight">
                                                        {`${o.common_name || o.name} (${o.patron_saint_feast_day || o.feast_day})`}
                                                    </span>
                                                    {renderIf(
                                                        o?.description,
                                                        <div
                                                            className="text-xs text-color-400 italic max-lines-2 tracking-wider leading-snug">
                                                            {o?.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    }}
                                    handleChange={(e) => {
                                        form.setValues({
                                            ...form.values,
                                            patron_saint: e.target,
                                            patron_saint_name: e.target.common_name || e.target.value,
                                            patron_saint_feast_day: e.target.feast_day,
                                            patron_saint_id: e.target.id
                                        });
                                    }}
                                />
                            )}
                        </Translate>
                    )
                },
            },
            {
                label: 'patron saint feast day',
                name: 'patron_saint_feast_day',
                component: compose(
                    withTranslate,
                    withProps(({value, onChange, translate}) => ({
                        onChange: (date, dateString) => {
                            onChange(createValue('patron_saint_feast_day', dateString))
                        },
                        value: !!value ? dayjs(value, "DD/MM") : null,
                        className: 'w-full',
                        suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                        name: 'patron saint feast day',
                        placeholder: translate('patron saint feast day'),
                        format: 'DD/MM'
                    }))
                )(DatePicker),
            },
        ],
    },
}

const validateTitleOrDepartmentGroupTitle = (name, keyName, t) => {
    const maxLength = {
        'title': 120,
        'group_title': 180
    }
    if (keyName !== 'title' && !name) return null;
    const rules = [
        (keyName === 'title') ? required(t) : null,
        lengthBetween(1, maxLength[keyName], t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
        firstLetterUppercase(t),
    ].filter(Boolean)
    const message = runRules(name, rules)
    if (message) return {[keyName]: message}
}

const validateDescription = (description, t) => {
    const rules = [
        lengthBetween(1, 500, t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ].filter(Boolean)
    const message = runRules(description, rules)
    if (message) return {description: message}
}

const validatePatronSaint = (patronSaintName, t) => {
    if (!patronSaintName) return null;
    const rules = [
        lengthBetween(2, 180, t),
        noNumber(t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
        firstLetterUppercase(t),
    ].filter(Boolean)
    const message = runRules(patronSaintName, rules)
    if (message) return {patron_saint: message}
}

const validate = (t) => (values) => {
    const {
        title,
        description,
        group_title,
        patron_saint
    } = values ?? {}
    const errorTitle = validateTitleOrDepartmentGroupTitle(title, 'title', t)
    const errorDepartmentGroupTitle = validateTitleOrDepartmentGroupTitle(group_title, 'group_title', t)
    const errorPatronSaint = validatePatronSaint(patron_saint ? patron_saint.value ?? patron_saint.common_name : undefined, t)
    const errorDescription = validateDescription(description, t)
    return {
        ...errorTitle,
        ...errorDepartmentGroupTitle,
        ...errorPatronSaint,
        ...errorDescription,
    }
}

export const OrganizationGroupCreateForm = ({
                                                organization,
                                                initialValues,
                                                onSubmit = NullAsDefault,
                                                isSubmitting = false,
                                            }) => {
    const t = useTranslate()
    const deptLabel = useOrgTypeLabel(organization?.org_type, 'group', organization?.label_privacy) || t('groups / ministries')

    const status_options = [StatusTypes.PENDING__1, StatusTypes.NORMAL, StatusTypes.CLOSED]

    const _initialValues = useMemo(
        () => ({
            title: undefined,
            description: undefined,
            active: 1,
            status: 1,
            privacy: 1,
            join_policy: 2,
            channel_post_permission: 2,
            organization,
            organization_id: organization?.id,
            ...(initialValues ?? {}),
            status_options: status_options.map(deepTranslate(t)),
        }),
        [organization, initialValues]
    )

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const {
                    ...rest
                } = values ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                        },
                        _.isUndefined
                    )
                )
            }}>
            <ActionModalWrapper
                className="gap-3"
                action={
                    <div className="flex items-center justify-end p-2 background">
                        <ResetButton disabled={isSubmitting}/>
                        <SubmitButton
                            label="create"
                            dirtyRequired={false}
                            loading={isSubmitting}
                        />
                    </div>
                }>
                <Pure>
                    <FieldsFactory
                        formSchema={[
                            formSchema.title,
                            formSchema.group_name,
                        ]}
                    />
                </Pure>

                <FormSection
                    icon={SaintsOutlineSvg}
                    titleKey="religious details">
                    <Pure>
                        <FieldsFactory formSchema={[formSchema.patron_saint]}/>
                    </Pure>
                </FormSection>

                <FormSection
                    icon={HiOutlineDocumentText}
                    titleKey="content">
                    <FieldsFactory formSchema={[formSchema.description]}/>
                </FormSection>

                {/*<FormSection*/}
                {/*    icon={BsShieldFillExclamation}*/}
                {/*    titleKey="settings">*/}
                {/*    <FieldsFactory*/}
                {/*        formSchema={[*/}
                {/*            formSchema.active,*/}
                {/*            formSchema.privacy,*/}
                {/*            formSchema.channel_post_permission,*/}
                {/*        ]}*/}
                {/*    />*/}
                {/*</FormSection>*/}

            </ActionModalWrapper>
        </Formik>
    )
}

export const OrganizationGroupUpdateForm = ({
                                                organization,
                                                initialValues,
                                                onSubmit = NullAsDefault,
                                                isSubmitting = false,
                                            }) => {
    const t = useTranslate()
    const deptLabel = useOrgTypeLabel(organization?.org_type, 'group', organization?.label_privacy) || t('groups / ministries')

    const status_options = [StatusTypes.PENDING__1, StatusTypes.NORMAL, StatusTypes.CLOSED]
    const organization_id = getId(organization)
    const _initialValues = useMemo(() => {
        const {about, ...rest} = initialValues ?? {}
        return {
            organization_id,
            organization,
            ...rest,
            status_options: status_options.map(deepTranslate(t)),
            about: convertFromString(about),
            patron_saint_id: _.get(rest, 'patron_saint.id', ''),
        }
    }, [organization_id, organization, initialValues]);

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, _initialValues)
                const {
                    about,
                    ...rest
                } = changes ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            about: about ? convertToString(about) : undefined,
                        },
                        _.isUndefined
                    )
                )
            }}>
            <ActionModalWrapper
                className="gap-3"
                action={
                    <div className="flex items-center justify-end p-2 background">
                        <ResetButton disabled={isSubmitting}/>
                        <SubmitButton
                            label="save"
                            dirtyRequired={false}
                            loading={isSubmitting}
                        />
                    </div>
                }>
                <Pure>
                    <FieldsFactory
                        formSchema={[
                            formSchema.title,
                            formSchema.group_name,
                        ]}
                    />
                </Pure>

                <FormSection
                    icon={SaintsOutlineSvg}
                    titleKey="religious details">
                    <FieldsFactory formSchema={[formSchema.patron_saint]}/>
                </FormSection>

                <FormSection
                    icon={HiOutlineDocumentText}
                    titleKey="content">
                    <FieldsFactory
                        formSchema={[
                            formSchema.description,
                            formSchema.about,
                        ]}
                    />
                </FormSection>

                <FormSection
                    icon={BsShieldFillExclamation}
                    titleKey="settings">
                    <FieldsFactory
                        formSchema={[
                            formSchema.active,
                            formSchema.privacy,
                            formSchema.channel_post_permission,
                            formSchema.public,
                        ]}
                    />
                    <RequiredSystemAdmin>
                        <FieldsFactory formSchema={[formSchema.public_by_server]}/>
                    </RequiredSystemAdmin>
                </FormSection>

                <FormSection
                    icon={RiInformationLine}
                    titleKey="details">
                    <FieldsFactory
                        formSchema={[
                            formSchema.group_type,
                            formSchema.idname,
                            formSchema.channel,
                            formSchema.join_code,
                        ]}
                    />
                </FormSection>

            </ActionModalWrapper>
        </Formik>
    )
}
